import { BudgetFeedback } from "./BudgetAlert";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import "moment/locale/th";

import Grid from "components/Grid/GridContainer.js";
import Col from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

import { BudgetService } from "_services/budget.service";

const styles = {
    cardTitleWhite: { color: "#FFFFFF", margin: 0, fontWeight: "300", fontFamily: '"Sarabun", sans-serif' },
    alertInfo: { backgroundColor: "#e0f7fa", color: "#006064", padding: "15px", borderRadius: "5px", marginBottom: "20px", borderLeft: "5px solid #00acc1" },
    alertDanger: { backgroundColor: "#ffebee", color: "#c62828", padding: "15px", borderRadius: "5px", marginBottom: "20px", borderLeft: "5px solid #e53935" }
};
const useStyles = makeStyles(styles);

const BudgetDashboard = () => {
    const classes = useStyles();
    const history = useHistory();

    const [error, setError] = useState("");
    const [activeRound, setActiveRound] = useState(null);
    const [requestList, setRequestList] = useState([]);
    const [isPlan, setIsPlan] = useState(false);
    const [userLevel, setUserLevel] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setIsLoading(true);
        setError("");
        try {
            const roundRes = await BudgetService.getActiveRound();
            if (roundRes.data.success && roundRes.data.data) {
                const round = roundRes.data.data;
                setActiveRound(round);

                // ดึงรายการทั้งหมดตามสิทธิ์
                const listRes = await BudgetService.getDashboardRequests(round.roundKey);
                if (listRes.data.success) {
                    setRequestList(listRes.data.data || []);
                    setUserLevel(listRes.data.userLevel || 0);
                    setIsPlan(listRes.data.isPlan === true);
                }
            }
        } catch (error) {
            setError(error.response?.data?.message || "ไม่สามารถโหลดข้อมูลคำของบประมาณได้ กรุณาลองใหม่");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateNew = () => {
        history.push({
            pathname: "/budget/request/form",
            state: { roundKey: activeRound?.roundKey }
        });
    };

    const handleEditRequest = (requestKey) => {
        history.push({
            pathname: "/budget/request/form",
            state: { roundKey: activeRound?.roundKey, requestKey: requestKey }
        });
    };

    const renderStatusBadge = (status) => {
        switch (status) {
            case "Draft": return <span className="badge badge-secondary p-2">ร่าง (Draft)</span>;
            case "Need_Revision": return <span className="badge badge-warning p-2">ขอให้แก้ไข</span>;
            case "Rejected": return <span className="badge badge-danger p-2">ไม่อนุมัติ</span>;
            case "Submitted": return <span className="badge badge-warning p-2">รอหัวหน้างานตรวจสอบ</span>;
            case "Section_Head_Approved": return <span className="badge badge-info p-2">รอหัวหน้ากลุ่มงานอนุมัติ</span>;
            case "Group_Head_Approved": return <span className="badge badge-primary p-2">รอรองภารกิจอนุมัติ</span>;
            case "Mission_Director_Approved": return <span className="badge badge-success p-2">ผ่านการเห็นชอบ (รอเข้ากรรมการ)</span>;
            default: return <span className="badge badge-light p-2">{status}</span>;
        }
    };

    // คำนวณยอดเงินรวมและจำนวนรายการของแต่ละคำขอ
    const calculateSummary = (req) => {
        let totalItems = req.items ? req.items.length : 0;
        let totalAmount = (req.items || []).reduce((sum, i) => sum + (parseFloat(i.totalAmount) || 0), 0);

        if (req.projects) {
            req.projects.forEach(p => {
                totalItems += (p.items ? p.items.length : 0);
                totalAmount += (p.items || []).reduce((sum, i) => sum + (parseFloat(i.totalAmount) || 0), 0);
            });
        }
        return { totalItems, totalAmount };
    };

    return (
        <div>
            <BudgetFeedback message={error} />
            <Grid>
                <Col xs={12}>
                    <Card>
                        <CardHeader color="info">
                            <h4 className={classes.cardTitleWhite}>ระบบบริหารจัดการและจัดทำแผนงบประมาณรายจ่ายเงินบำรุง</h4>
                        </CardHeader>
                        <CardBody>
                            {isLoading ? (
                                <p className="text-center p-4">กำลังโหลดข้อมูลรอบงบประมาณ...</p>
                            ) : activeRound ? (
                                <div className={classes.alertInfo}>
                                    <h5><i className="fas fa-bullhorn"></i> <strong>เปิดรับคำขอ:</strong> {activeRound.roundName} (ปี {activeRound.calYear})</h5>
                                    <p style={{ margin: 0 }}>
                                        เปิดระบบ: {moment(activeRound.startDate).format("LL")} ถึง {moment(activeRound.endDate).format("LL")}
                                    </p>
                                </div>
                            ) : (
                                <div className={classes.alertDanger}>
                                    <h5><i className="fas fa-lock"></i> <strong>ระบบปิดรับคำขอ</strong></h5>
                                    <p style={{ margin: 0 }}>ขณะนี้ไม่อยู่ในช่วงเวลาการเปิดรับคำของบประมาณ</p>
                                </div>
                            )}

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="font-weight-bold text-info m-0">
                                    <i className="fas fa-list-alt"></i> รายการคำของบประมาณ {isPlan ? "(ทุกหน่วยงาน)" : userLevel >= 3 ? "(ภารกิจของฉัน)" : userLevel === 2 ? "(กลุ่มงานของฉัน)" : "(หน่วยงานของฉัน)"}
                                </h5>
                                <Button color="primary" onClick={handleCreateNew} disabled={!activeRound}>
                                    <i className="fas fa-plus"></i> บันทึกคำขอใหม่
                                </Button>
                            </div>

                            {/* ตารางรายการคำขอ */}
                            <table className="table table-bordered table-striped" style={{ fontSize: "13px" }}>
                                <thead className="thead-light">
                                    <tr>
                                        <th width="40" className="text-center">#</th>
                                        <th>ชื่อโครงการ</th>
                                        <th>หน่วยงานที่ขอ</th>
                                        <th width="160">ชื่อผู้ขอ</th>
                                        <th width="140" className="text-right">งบประมาณรวม</th>
                                        <th width="180" className="text-center">สถานะ</th>
                                        <th width="110" className="text-center">จัดการ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requestList.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center p-4 text-muted">
                                                ยังไม่มีรายการคำขอในรอบนี้
                                            </td>
                                        </tr>
                                    ) : (
                                        requestList.map((req, idx) => {
                                            const { totalAmount } = calculateSummary(req);
                                            const projectNames = (req.projects || req.Projects || [])
                                                .map(project => project.projectName || project.ProjectName).filter(Boolean);
                                            const projectName = req.projectName || (projectNames.length ? projectNames.join(" / ") : "รายการคำขอทั่วไป");

                                            return (
                                                <tr key={req.requestKey}>
                                                    <td className="text-center">{idx + 1}</td>
                                                    <td>
                                                        <strong>{projectName}</strong>
                                                        <div className="text-muted" style={{ fontSize: "11px" }}>
                                                            รหัสคำขอ: #{req.requestKey} | วันที่: {moment(req.createDT).format("D MMM YYYY")}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <strong>{req.departmentName || "ไม่ระบุหน่วยงาน"}</strong>
                                                        <div className="text-muted">กลุ่มงาน: {req.groupWorkName || "—"}</div>
                                                        <div className="text-muted">ภารกิจ: {req.missionName || "—"}</div>
                                                    </td>
                                                    <td>{req.requesterName || req.requesterCode}</td>
                                                    <td className="text-right font-weight-bold text-primary">
                                                        {Number(req.totalAmount ?? totalAmount).toLocaleString()} ฿
                                                    </td>
                                                    <td className="text-center">
                                                        {renderStatusBadge(req.status)}
                                                    </td>
                                                    <td className="text-center">
                                                        {(req.status === "Draft" || req.status === "Need_Revision") ? (
                                                            <button 
                                                                className="btn btn-sm btn-warning p-1 px-2"
                                                                onClick={() => handleEditRequest(req.requestKey)}
                                                                title="แก้ไขคำขอร่าง"
                                                            >
                                                                <i className="fas fa-edit"></i> แก้ไขต่อ
                                                            </button>
                                                        ) : (
                                                            <button 
                                                                className="btn btn-sm btn-info p-1 px-2"
                                                                onClick={() => handleEditRequest(req.requestKey)}
                                                                title="ดูรายละเอียด/ติดตามสถานะ"
                                                            >
                                                                <i className="fas fa-eye"></i> ดูรายละเอียด
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>

                        </CardBody>
                    </Card>
                </Col>
            </Grid>
        </div>
    );
};

export default BudgetDashboard;